import React from 'react';
import { Shovel, AlertTriangle, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
export function ProblemSection() {
  const problems = [{
    icon: Shovel,
    title: 'The Shovelling Burden',
    description: "Hours spent in freezing temperatures clearing heavy snow. It's physically demanding, time-consuming, and risks back injury or heart strain."
  }, {
    icon: AlertTriangle,
    title: 'Safety Hazards',
    description: 'Icy patches on your driveway are a major liability. Slips and falls can lead to serious injuries for your family, guests, or delivery drivers.'
  }, {
    icon: DollarSign,
    title: 'Recurring Costs',
    description: 'Paying for seasonal snow removal contracts adds up year after year. Plus, salt and chemicals damage your concrete and landscaping over time.'
  }];
  return <section className="py-16 bg-slate-50 md:py-24 overflow-hidden" data-id="element-244">
      <div className="container mx-auto px-4 md:px-6" data-id="element-245">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-246">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-id="element-247">
            Winter in Ontario is Tough Enough
          </h2>
          <p className="mt-4 text-lg text-slate-600" data-id="element-248">
            Don't let your driveway add to the stress. Traditional snow removal
            methods are outdated, dangerous, and exhausting.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-249">
          {/* Left Column: Problems List */}
          <div className="space-y-6" data-id="element-250">
            {problems.map((problem, index) => <Card key={index} className="border-none shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group bg-white hover:border-slate-300 hover:shadow-slate-200/50" data-id="element-251">
                <div className="flex flex-col sm:flex-row items-start p-2" data-id="element-252">
                  <CardHeader className="pb-2 sm:pb-4" data-id="element-253">
                    <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center mb-0 group-hover:bg-slate-200 transition-colors" data-id="element-254">
                      <problem.icon className="h-6 w-6 text-slate-900 group-hover:scale-110 transition-transform duration-300" data-id="element-255" />
                    </div>
                  </CardHeader>
                  <div className="flex-1" data-id="element-256">
                    <CardHeader className="pt-4 sm:pt-6 pb-2 pl-0 sm:pl-2" data-id="element-257">
                      <CardTitle className="text-xl group-hover:text-slate-900 transition-colors" data-id="element-258">
                        {problem.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pl-0 sm:pl-2 pt-0" data-id="element-259">
                      <p className="text-slate-600 leading-relaxed" data-id="element-260">
                        {problem.description}
                      </p>
                    </CardContent>
                  </div>
                </div>
              </Card>)}
          </div>

          {/* Right Column: System Active Card (Moved from Hero) */}
          <div className="relative mx-auto w-full max-w-[500px] lg:max-w-none animate-in fade-in slide-in-from-right-8 duration-1000 delay-200" data-id="element-261">
            <div className="relative aspect-square overflow-hidden rounded-2xl bg-slate-800 shadow-2xl border border-slate-700" data-id="element-262">
              {/* Background Image */}
              <img src="https://cdn.magicpatterns.com/uploads/tcEtmEEvVo3B1T443U3Urc/62q_tp9hOMoWaFVIiGUZ3.png" alt="Heated driveway in winter" className="absolute inset-0 h-full w-full object-cover" data-id="element-263" />

              {/* Interactive-looking placeholder overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-slate-950/95 via-slate-900/80 to-slate-900/30" data-id="element-264">
                <div className="text-center p-8 w-full" data-id="element-265">
                  <div className="mb-6 inline-flex h-24 w-24 items-center justify-center rounded-full bg-primary/30 ring-4 ring-primary/20 animate-pulse backdrop-blur-md shadow-[0_0_30px_rgba(14,165,233,0.3)]" data-id="element-266">
                    <svg className="h-12 w-12 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-267">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" data-id="element-268" />
                    </svg>
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-2 drop-shadow-md" data-id="element-269">
                    System Active
                  </h3>
                  <p className="text-slate-200 font-medium mb-8 drop-shadow-sm" data-id="element-270">
                    Melting snow automatically
                  </p>

                  <div className="grid grid-cols-2 gap-4" data-id="element-271">
                    <div className="rounded-xl bg-slate-950/80 p-4 backdrop-blur-md border border-slate-700 transform transition-transform hover:scale-105 shadow-lg" data-id="element-272">
                      <p className="text-3xl font-bold text-white" data-id="element-273">-20°C</p>
                      <p className="text-xs text-slate-400 uppercase tracking-wider mt-1 font-semibold" data-id="element-274">
                        Outside Temp
                      </p>
                    </div>
                    <div className="rounded-xl bg-primary/90 p-4 backdrop-blur-md border border-primary/50 transform transition-transform hover:scale-105 shadow-lg shadow-primary/20" data-id="element-275">
                      <p className="text-3xl font-bold text-white" data-id="element-276">+3°C</p>
                      <p className="text-xs text-primary-foreground/80 uppercase tracking-wider mt-1 font-semibold" data-id="element-277">
                        Surface Temp
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Decorative blob behind card */}
            <div className="absolute -inset-4 bg-primary/20 blur-3xl -z-10 rounded-full" data-id="element-278"></div>
          </div>
        </div>
      </div>
    </section>;
}